import React, { createContext, useContext, useState, useEffect } from 'react'
import { clearToken, getToken, invokeFunction, setToken } from '../services/supabase'

const AuthContext = createContext()

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = getToken()
    if (token) {
      verifyToken()
    } else {
      setLoading(false)
    }
  }, [])

  const verifyToken = async () => {
    try {
      const data = await invokeFunction('auth', { action: 'verify' })
      setUser(data.user)
    } catch (error) {
      console.error('Token verification failed:', error)
      clearToken()
    } finally {
      setLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      const data = await invokeFunction('auth', {
        action: 'login',
        name: credentials.name,
        password: credentials.password,
      })
      setToken(data.token)
      setUser(data.user)
      return { success: true }
    } catch (error) {
      console.error('Login failed:', error)
      return {
        success: false,
        error: error.data?.error || error.message || 'Login failed',
      }
    }
  }

  const register = async (userData) => {
    try {
      const data = await invokeFunction('auth', {
        action: 'register',
        name: userData.name,
        password: userData.password,
      })
      return { success: true, data }
    } catch (error) {
      console.error('Registration failed:', error)
      return {
        success: false,
        error: error.data?.error || error.message || 'Registration failed',
      }
    }
  }

  const logout = () => {
    clearToken()
    setUser(null)
  }

  const value = {
    user,
    loading,
    login,
    register,
    logout,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
